import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FeaturedProjectsCarousel = ({ projects }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        handleNext();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [currentIndex, isPaused]);

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % projects?.length);
  };

  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + projects?.length) % projects?.length);
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  return (
    <div className="bg-card rounded-xl overflow-hidden border border-border">
      <div className="p-4 md:p-6 lg:p-8">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-3 md:mb-4 text-center"
        >
          Featured Success Stories
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-sm md:text-base lg:text-lg text-muted-foreground text-center mb-6 md:mb-8 lg:mb-12"
        >
          Real-world projects showcasing our expertise and client satisfaction
        </motion.p>
      </div>
      <div
        className="relative"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={currentIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 p-4 md:p-6 lg:p-8"
          >
            <div className="relative h-64 md:h-80 lg:h-96 rounded-xl overflow-hidden">
              <Image
                src={projects?.[currentIndex]?.image}
                alt={projects?.[currentIndex]?.imageAlt}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center gap-2 flex-wrap">
                  {projects?.[currentIndex]?.tags?.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-primary/90 text-primary-foreground text-xs md:text-sm rounded-full backdrop-blur-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-center space-y-4 md:space-y-6">
              <div>
                <h3 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-2 md:mb-3">
                  {projects?.[currentIndex]?.title}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground mb-4 md:mb-6">
                  {projects?.[currentIndex]?.description}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div className="bg-muted rounded-lg p-3 md:p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Target" size={16} color="var(--color-primary)" />
                    <span className="text-xs md:text-sm font-medium text-foreground">Industry</span>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground">{projects?.[currentIndex]?.industry}</p>
                </div>
                <div className="bg-muted rounded-lg p-3 md:p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Calendar" size={16} color="var(--color-success)" />
                    <span className="text-xs md:text-sm font-medium text-foreground">Duration</span>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground">{projects?.[currentIndex]?.duration}</p>
                </div>
              </div>

              <div className="space-y-2 md:space-y-3">
                <p className="text-xs md:text-sm font-medium text-foreground">Key Results:</p>
                {projects?.[currentIndex]?.results?.map((result, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <Icon name="TrendingUp" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                    <span className="text-xs md:text-sm text-muted-foreground">{result}</span>
                  </div>
                ))}
              </div>

              <div className="bg-muted rounded-lg p-4 md:p-6">
                <div className="flex items-start gap-3">
                  <Icon name="Quote" size={20} color="var(--color-primary)" className="flex-shrink-0" />
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground italic mb-2 md:mb-3">
                      "{projects?.[currentIndex]?.testimonial}"
                    </p>
                    <p className="text-xs md:text-sm font-medium text-foreground">
                      {projects?.[currentIndex]?.clientName}
                    </p>
                    <p className="text-xs text-muted-foreground">{projects?.[currentIndex]?.clientRole}</p>
                  </div>
                </div>
              </div>

              <Button variant="default" iconName="ExternalLink" iconPosition="right" className="w-full sm:w-auto">
                View Case Study
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>

        <button
          onClick={handlePrev}
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-card border border-border flex items-center justify-center hover:bg-primary hover:border-primary transition-all duration-300 z-10"
          aria-label="Previous project"
        >
          <Icon name="ChevronLeft" size={20} color="currentColor" />
        </button>

        <button
          onClick={handleNext}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-card border border-border flex items-center justify-center hover:bg-primary hover:border-primary transition-all duration-300 z-10"
          aria-label="Next project"
        >
          <Icon name="ChevronRight" size={20} color="currentColor" />
        </button>
      </div>
      <div className="flex items-center justify-center gap-2 p-4 md:p-6">
        {projects?.map((_, idx) => (
          <button
            key={idx}
            onClick={() => {
              setDirection(idx > currentIndex ? 1 : -1);
              setCurrentIndex(idx);
            }}
            className={`h-2 rounded-full transition-all duration-300 ${
              idx === currentIndex ? 'w-8 bg-primary' : 'w-2 bg-muted'
            }`}
            aria-label={`Go to project ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default FeaturedProjectsCarousel;